/**
 * Общий разбор контура из текста/файла: GeoJSON или список координат (WGS84).
 * Используется ОИ (главная карта) и РД.
 */

export type CoordinateOrder = "lonlat" | "latlon";

export type GeometryCoordsParseResult =
  | {
      ok: true;
      feature: GeoJSON.Feature;
      /** Сколько Feature было в FeatureCollection (если > 1 — импортирована первая подходящая). */
      featureCount: number;
      /** Индекс (0-based) выбранной фичи в коллекции; 0 для одиночного Geometry/Feature. */
      selectedFeatureIndex: number;
    }
  | { ok: false; error: string };

/** Лимит размера текста (байты UTF-16 length ≈ chars) — защита браузера. */
export const GEOMETRY_COORDS_IMPORT_MAX_CHARS = 2 * 1024 * 1024;

const GEOMETRY_TYPES = new Set([
  "Point",
  "LineString",
  "Polygon",
  "MultiPoint",
  "MultiLineString",
  "MultiPolygon",
]);

const PREFERRED_GEOMETRY_ORDER = [
  "Polygon",
  "MultiPolygon",
  "LineString",
  "MultiLineString",
  "Point",
  "MultiPoint",
] as const;

function stripBom(s: string): string {
  return s.replace(/^\uFEFF/, "");
}

function isGeometryObject(data: unknown): data is GeoJSON.Geometry {
  if (!data || typeof data !== "object") return false;
  const t = (data as Record<string, unknown>).type;
  return typeof t === "string" && GEOMETRY_TYPES.has(t);
}

function featureFromGeometry(geometry: GeoJSON.Geometry): GeoJSON.Feature {
  return { type: "Feature", properties: {}, geometry };
}

/**
 * Выбирает первую подходящую геометрию из GeoJSON.
 * Для FeatureCollection предпочитает полигон → линия → точка; иначе берёт первую валидную.
 */
function normalizeToFeature(
  data: unknown,
): { feature: GeoJSON.Feature; featureCount: number; selectedFeatureIndex: number } | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  const t = d.type as string | undefined;

  if (t === "Feature") {
    const geom = d.geometry;
    if (!isGeometryObject(geom)) return null;
    return {
      feature: data as GeoJSON.Feature,
      featureCount: 1,
      selectedFeatureIndex: 0,
    };
  }

  if (t === "FeatureCollection") {
    const features = d.features;
    if (!Array.isArray(features) || features.length === 0) return null;
    const valid: { feature: GeoJSON.Feature; index: number }[] = [];
    for (let i = 0; i < features.length; i++) {
      const item = features[i];
      if (!item || typeof item !== "object") continue;
      const g = (item as GeoJSON.Feature).geometry;
      if (!isGeometryObject(g)) continue;
      valid.push({ feature: item as GeoJSON.Feature, index: i });
    }
    if (valid.length === 0) return null;
    for (const preferred of PREFERRED_GEOMETRY_ORDER) {
      const hit = valid.find((v) => v.feature.geometry?.type === preferred);
      if (hit) {
        return {
          feature: hit.feature,
          featureCount: features.length,
          selectedFeatureIndex: hit.index,
        };
      }
    }
    const first = valid[0];
    return {
      feature: first.feature,
      featureCount: features.length,
      selectedFeatureIndex: first.index,
    };
  }

  if (t && GEOMETRY_TYPES.has(t) && isGeometryObject(data)) {
    return {
      feature: featureFromGeometry(data),
      featureCount: 1,
      selectedFeatureIndex: 0,
    };
  }

  return null;
}

function tryParseGeoJSON(
  raw: string,
):
  | { feature: GeoJSON.Feature; featureCount: number; selectedFeatureIndex: number }
  | null
  | "invalid-json" {
  const trimmed = stripBom(raw.trim());
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    const data = JSON.parse(trimmed) as unknown;
    const normalized = normalizeToFeature(data);
    return normalized ?? null;
  } catch {
    return "invalid-json";
  }
}

function parseTwoNumbers(line: string): [number, number] | null {
  const cleaned = line.replace(/#/g, "").trim();
  if (!cleaned) return null;
  const parts = cleaned.split(/[\s,;]+/).filter(Boolean);
  if (parts.length < 2) return null;
  const a = Number(parts[0].replace(",", "."));
  const b = Number(parts[1].replace(",", "."));
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return [a, b];
}

function validateLonLat(lon: number, lat: number): void {
  if (lon < -180 || lon > 180 || lat < -90 || lat > 90) {
    throw new Error("Координаты вне допустимого диапазона (долгота −180…180, широта −90…90).");
  }
}

/** Строит Feature из строк вида «долгота широта» по одной точке в строке. */
function parseCoordinateLines(raw: string, order: CoordinateOrder): GeoJSON.Feature {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith("#"));

  const coords: [number, number][] = [];
  for (const line of lines) {
    const pair = parseTwoNumbers(line);
    if (!pair) {
      throw new Error(
        `Не удалось разобрать строку координат: «${line}». Ожидаются два числа (например 37.61 55.75).`,
      );
    }
    const [a, b] = pair;
    const lon = order === "latlon" ? b : a;
    const lat = order === "latlon" ? a : b;
    validateLonLat(lon, lat);
    coords.push([lon, lat]);
  }

  if (coords.length === 0) {
    throw new Error("Не найдено ни одной пары координат. Укажите две цифры в строке (долгота и широта).");
  }

  if (coords.length === 1) {
    return {
      type: "Feature",
      properties: {},
      geometry: { type: "Point", coordinates: coords[0] },
    };
  }

  if (coords.length === 2) {
    return {
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: coords },
    };
  }

  let ring = [...coords];
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring = [...ring, first];
  }
  return {
    type: "Feature",
    properties: {},
    geometry: { type: "Polygon", coordinates: [ring] },
  };
}

function validateFeatureGeometry(feature: GeoJSON.Feature): string | null {
  const g = feature.geometry;
  if (!g || typeof g !== "object") {
    return "В объекте GeoJSON нет поля geometry.";
  }
  const t = g.type;
  if (!GEOMETRY_TYPES.has(t)) {
    return `Неподдерживаемый тип геометрии в GeoJSON: ${String(t)}.`;
  }
  return null;
}

/**
 * Разбирает текст: GeoJSON (Geometry / Feature / FeatureCollection) или построчные координаты.
 */
export function parseGeometryCoordsFromText(
  raw: string,
  options?: { coordinateOrder?: CoordinateOrder; maxChars?: number },
): GeometryCoordsParseResult {
  const maxChars = options?.maxChars ?? GEOMETRY_COORDS_IMPORT_MAX_CHARS;
  if (raw.length > maxChars) {
    return {
      ok: false,
      error: `Текст слишком большой (больше ${Math.round(maxChars / 1024 / 1024)} МБ). Уменьшите файл или вставьте фрагмент.`,
    };
  }

  const trimmed = stripBom(raw.trim());
  if (!trimmed) {
    return { ok: false, error: "Введите текст в поле ниже или загрузите файл." };
  }

  const gj = tryParseGeoJSON(trimmed);
  if (gj === "invalid-json") {
    return { ok: false, error: "Некорректный JSON. Проверьте запятую, кавычки и скобки." };
  }
  if (gj) {
    const err = validateFeatureGeometry(gj.feature);
    if (err) return { ok: false, error: err };
    return {
      ok: true,
      feature: gj.feature,
      featureCount: gj.featureCount,
      selectedFeatureIndex: gj.selectedFeatureIndex,
    };
  }

  try {
    const order = options?.coordinateOrder ?? "lonlat";
    const feature = parseCoordinateLines(trimmed, order);
    return { ok: true, feature, featureCount: 1, selectedFeatureIndex: 0 };
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Не удалось разобрать координаты.";
    return {
      ok: false,
      error: `${msg} Если это GeoJSON, убедитесь, что текст начинается с «{» или «[».`,
    };
  }
}

/** Инструмент рисования по типу геометрии импорта (для диалога KLS). */
export function inferDrawToolFromFeature(feature: GeoJSON.Feature): string {
  const t = feature.geometry?.type;
  switch (t) {
    case "Point":
    case "MultiPoint":
      return "draw-point";
    case "LineString":
    case "MultiLineString":
      return "draw-line";
    default:
      return "draw-polygon";
  }
}

/** Код примитива KLS-ориентир по типу GeoJSON. */
export function inferPrimitiveCodeFromFeature(feature: GeoJSON.Feature): string {
  const t = feature.geometry?.type;
  switch (t) {
    case "Point":
      return "point";
    case "MultiPoint":
      return "multipoint";
    case "LineString":
      return "linestring";
    case "MultiLineString":
      return "multilinestring";
    case "MultiPolygon":
      return "multipolygon";
    default:
      return "polygon";
  }
}

/** Пример полигона вокруг центра Москвы — шаблон для скачивания / правки. */
export const GEOMETRY_COORDS_GEOJSON_TEMPLATE = `{
  "type": "Feature",
  "properties": {
    "name": "Пример контура — замените координаты"
  },
  "geometry": {
    "type": "Polygon",
    "coordinates": [
      [
        [37.60, 55.75],
        [37.62, 55.75],
        [37.62, 55.76],
        [37.60, 55.76],
        [37.60, 55.75]
      ]
    ]
  }
}
`;

/** Построчный шаблон координат (долгота широта), совместим с порядком lonlat. */
export const GEOMETRY_COORDS_TXT_TEMPLATE_LONLAT = `# Одна точка на строку: долгота широта (WGS84)
# Строки с # игнорируются. ≥3 точек → полигон (замыкается автоматически).
37.60 55.75
37.62 55.75
37.62 55.76
37.60 55.76
`;

/** Построчный шаблон (широта долгота). */
export const GEOMETRY_COORDS_TXT_TEMPLATE_LATLON = `# Одна точка на строку: широта долгота (WGS84)
# Перед импортом выберите порядок «Широта, долгота».
55.75 37.60
55.75 37.62
55.76 37.62
55.76 37.60
`;

/** Скачать текстовый шаблон в браузере (без сервера). */
export function downloadGeometryCoordsTemplate(filename: string, content: string, mime = "text/plain;charset=utf-8"): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
